import svgPaths from "../imports/svg-jc3lxe7epg";
import { motion, type Transition } from "framer-motion";
import type { ReactNode } from "react";
import BurgerArrow from "./BurgerArrow";

interface StacykidsBurgerProps {
  isExploded: boolean;
}

const enterTransition: Transition = {
  duration: 0.3,
  ease: [0.4, 0, 0.2, 1],
};

const leaveTransition: Transition = {
  type: "spring",
  stiffness: 550,
  damping: 18,
  mass: 1,
};

function getTransition(isExploded: boolean): Transition {
  return isExploded ? enterTransition : leaveTransition;
}

function LabelText({
  lines,
  className = "",
  lineClassNames = [],
}: {
  lines: string[];
  className?: string;
  lineClassNames?: string[];
}) {
  return (
    <div
      className={[
        "flex flex-col justify-center whitespace-nowrap",
        "font-['Diary_Notes',sans-serif] text-[#f2f1de]",
        "text-[8px] leading-[8px] tracking-[0.16px]",
        "min-[380px]:text-[9px] min-[380px]:leading-[9px] min-[380px]:tracking-[0.18px]",
        "sm:text-[10px] sm:leading-[10px] sm:tracking-[0.2px]",
        "lg:text-[12px] lg:leading-[12px] lg:tracking-[0.24px]",
        className,
      ].join(" ")}
    >
      {lines.map((line, index) => (
        <p
          key={`${line}-${index}`}
          className={["mb-0", lineClassNames[index] ?? ""].join(" ")}
        >
          {line}
        </p>
      ))}
    </div>
  );
}
function FlowLabel({
  isExploded,
  side,
  inset = 0,
  children,
}: {
  isExploded: boolean;
  side: "left" | "right";
  inset?: number;
  children?: ReactNode;
}) {
  const explodedX = side === "left" ? inset : -inset;
  const hiddenX = side === "left" ? explodedX - 8 : explodedX + 8;

  return (
    <motion.div
      initial={false}
      className={[
        "pointer-events-none flex shrink-0 overflow-visible",
        side === "left" ? "justify-end" : "justify-start",
      ].join(" ")}
      animate={{
        opacity: isExploded && children ? 1 : 0,
        x: isExploded && children ? explodedX : hiddenX,
      }}
      transition={getTransition(isExploded)}
      aria-hidden={!isExploded}
    >
      <div className="flex items-center gap-2 whitespace-nowrap">{children}</div>
    </motion.div>
  );
}

function SideLabel({
  isExploded,
  side,
  lines,
  arrowDirection,
  arrowColor,
}: {
  isExploded: boolean;
  side: "left" | "right";
  lines: string[];
  arrowDirection: "left" | "right" | "left-bottom" | "right-top" | "right-bottom";
  arrowColor?: string;
}) {
  return (
    <FlowLabel isExploded={isExploded} side={side} inset={24}>
      {side === "left" ? (
        <>
          <LabelText lines={lines} />
          <BurgerArrow direction={arrowDirection} color={arrowColor} />
        </>
      ) : (
        <>
          <BurgerArrow direction={arrowDirection} color={arrowColor} />
          <LabelText lines={lines} />
        </>
      )}
    </FlowLabel>
  );
}

function DualRightLabels({ isExploded }: { isExploded: boolean }) {
  return (
    <FlowLabel isExploded={isExploded} side="right" inset={24}>
      <div className="flex flex-col items-start gap-2">
        <div className="flex items-center gap-2">
          <BurgerArrow direction="right-top" color="var(--beige-500)" />
          <LabelText lines={["CHEDDAR", "X2"]} />
        </div>

        <div className="flex items-center gap-2">
          <BurgerArrow direction="right-bottom" color="var(--beige-500)" />
          <LabelText lines={["CARNE"]} />
        </div>
      </div>
    </FlowLabel>
  );
}

function LayerRow({
  isExploded,
  y,
  left,
  right,
  children,
}: {
  isExploded: boolean;
  y: number;
  left?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
}) {
  return (
    <motion.div
      className="
        mx-auto grid w-fit items-center justify-center gap-x-1
        grid-cols-[68px_133px_68px]
        sm:grid-cols-[80px_133px_80px]
        md:grid-cols-[94px_133px_94px]
      "
      animate={{ y: isExploded ? y : 0 }}
      transition={getTransition(isExploded)}
    >
      <div className="flex h-0 items-center justify-end overflow-visible">
        {left ?? null}
      </div>

      <div className="flex items-center justify-center">{children}</div>

      <div className="flex h-0 items-center justify-start overflow-visible">
        {right ?? null}
      </div>
    </motion.div>
  );
}

function TopBun() {
  return (
    <div className="w-[108px] py-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[36px] rounded-tl-[80px] rounded-tr-[80px] rounded-bl-[8px] rounded-br-[8px] border-4 border-beige-500 sm:h-[40px] md:h-[43.75px]" />
    </div>
  );
}

function MeatCheeseLayer() {
  return (
    <div className="w-[108px] p-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[16px] sm:h-[18px] md:h-[19px]">
        <svg
          className="block h-full w-full overflow-visible"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 133 23"
        >
          <g>
            <path d={svgPaths.p36715d00} fill="var(--beige-500)" />
            <path
              d={svgPaths.p13a95540}
              stroke="var(--beige-500)"
              strokeLinecap="round"
              strokeWidth="4"
            />
            <path
              d={svgPaths.pca7c900}
              stroke="var(--beige-500)"
              strokeLinecap="round"
              strokeWidth="4"
            />
          </g>
        </svg>
      </div>
    </div>
  );
}

function BottomBun() {
  return (
    <div className="w-[108px] py-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[16px] rounded-[8px] border-4 border-beige-500 sm:h-[17px] md:h-[18.75px]" />
    </div>
  );
}

function SubtitleContainer() {
  return (
    <div className="flex shrink-0 flex-col items-center justify-center">
      <div className="mb-1 font-['Diary_Notes',sans-serif] text-[14px] leading-[14px] tracking-[0.32px] text-beige-500 sm:text-[15px] sm:leading-[15px] md:text-[16px] md:leading-[16px]">
        KIDS
      </div>

      <div className="relative h-[13px] w-[32px]">
        <svg
          className="absolute inset-0 h-full w-full"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 32 13.0841"
        >
          <g>
            <path d={svgPaths.p18a42100} fill="var(--beige-500)" />
            <path d={svgPaths.p7f16df0} fill="var(--beige-500)" />
            <path d={svgPaths.p32f12000} fill="var(--beige-500)" />
            <path d={svgPaths.p1e6bff00} fill="var(--beige-500)" />
            <path d={svgPaths.pb156080} fill="var(--beige-500)" />
            <path d={svgPaths.p35a3d1f0} fill="var(--beige-500)" />
            <path d={svgPaths.p4d6cd00} fill="var(--beige-500)" />
            <path d={svgPaths.p38f88800} fill="var(--beige-500)" />
            <path d={svgPaths.p27923d80} fill="var(--beige-500)" />
          </g>
        </svg>

        <svg
          className="absolute left-[46%] top-[45%] h-[3.83px] w-[7.33px]"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 7.3344 3.83543"
        >
          <g>
            <path d={svgPaths.pd3d1100} fill="var(--beige-500)" />
            <path d={svgPaths.p1c8c2f80} fill="var(--beige-500)" />
          </g>
        </svg>
      </div>
    </div>
  );
}

export default function StacykidsBurger({ isExploded }: StacykidsBurgerProps) {
  const transition = getTransition(isExploded);

  return (
    <div className="mx-auto flex h-full w-full max-w-[420px] flex-col items-center justify-center px-2 sm:px-3">
      <div className="flex w-full flex-col items-center justify-center gap-0">
        <LayerRow
          isExploded={isExploded}
          y={-20}
          left={
            <SideLabel
              isExploded={isExploded}
              side="left"
              lines={["Pan ", "BRIOCHE"]}
              arrowDirection="left"
              arrowColor="var(--beige-500)"
            />
          }
        >
          <TopBun />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={0}
          right={<DualRightLabels isExploded={isExploded} />}
        >
          <MeatCheeseLayer />
        </LayerRow>

        <motion.div
          className="
            mx-auto grid w-fit items-center justify-center gap-x-1
            grid-cols-[68px_133px_68px]
            sm:grid-cols-[80px_133px_80px]
            md:grid-cols-[94px_133px_94px]
          "
          animate={{ y: isExploded ? 20 : 0 }}
          transition={transition}
        >
          <div />
          <div className="flex justify-center">
            <BottomBun />
          </div>
          <div />
        </motion.div>
      </div>

      <motion.div
        className="mt-4 flex w-full flex-row items-end justify-center gap-4"
        animate={{ y: isExploded ? 40 : 0 }}
        transition={transition}
      >
        <p className="whitespace-nowrap font-['Roboto_Mono',monospace] text-[28px] font-bold leading-none tracking-[0.06em] text-beige-500 sm:text-[36px] md:text-[44px] lg:text-[48px]">
          STACYKIDS
        </p>

        <div className="pb-2">
          <SubtitleContainer />
        </div>
      </motion.div>
    </div>
  );
}

export function StacykidsBurgerClosed() {
  return <StacykidsBurger isExploded={false} />;
}

export function StacykidsBurgerExploded() {
  return <StacykidsBurger isExploded={true} />;
}